import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/axios'
import { useAuth } from '../../context/AuthContext'
import { PageHeader, StatCard, Card, Spinner, EmptyState } from '../../components/ui'

export default function Dashboard() {
  const { user } = useAuth()
  const [stats, setStats]       = useState(null)
  const [activity, setActivity] = useState(null)
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    Promise.all([api.get('/dashboard/stats'), api.get('/dashboard/activity')])
      .then(([s, a]) => { setStats(s.data); setActivity(a.data) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const statCards = stats ? [
    { label: 'Projects',        value: stats.projects,       icon: '⬡', gradient: 'bg-purple-500/20 text-purple-300' },
    { label: 'Repositories',    value: stats.repositories,   icon: '⌁', gradient: 'bg-blue-500/20 text-blue-300' },
    { label: 'Documents',       value: stats.documents,      icon: '◻', gradient: 'bg-cyan-500/20 text-cyan-300' },
    { label: 'Test Plans',      value: stats.test_plans,     icon: '✓', gradient: 'bg-emerald-500/20 text-emerald-300' },
    { label: 'Specifications',  value: stats.specifications, icon: '◈', gradient: 'bg-pink-500/20 text-pink-300' },
  ] : []

  return (
    <div className="pb-10 animate-fade-in"
      style={{ background: 'linear-gradient(180deg, #0d0521 0%, #07020f 100%)', minHeight: '100%' }}>

      <PageHeader
        title={`Good ${new Date().getHours() < 12 ? 'morning' : 'afternoon'}, ${user?.name?.split(' ')[0]} 👋`}
        subtitle="Here's what's happening with your repositories">
        <Link to="/dashboard/projects" className="btn-specze-sm text-white">
          + New Project
        </Link>
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>
      ) : (
        <div className="px-8 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {statCards.map(s => <StatCard key={s.label} {...s} />)}
          </div>

          {/* Plan banner */}
          {user?.plan === 'free' && (
            <div className="rounded-2xl p-4 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(168,85,247,0.08))', border: '1px solid rgba(168,85,247,0.2)' }}>
              <div>
                <p className="font-semibold text-white text-sm">You're on the Free plan</p>
                <p className="text-xs text-white/50 mt-0.5">Upgrade to Pro for unlimited documents and repositories.</p>
              </div>
              <a href="#pricing" className="btn-specze-sm text-white flex-shrink-0">Upgrade to Pro</a>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Documents */}
            <Card>
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/6">
                <h2 className="font-semibold text-white text-sm">Recent Documents</h2>
                <Link to="/dashboard/documents" className="text-xs text-purple-400 hover:text-purple-300">View all →</Link>
              </div>
              {!activity?.recent_documents?.length
                ? <EmptyState icon="📄" title="No documents yet" description="Generate your first document from a repository." />
                : <div className="divide-y divide-white/5">
                    {activity.recent_documents.slice(0, 5).map(doc => (
                      <div key={doc.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-white/2 transition-colors">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: 'rgba(139,63,255,0.15)', border: '1px solid rgba(139,63,255,0.2)' }}>
                          <span className="text-sm">📄</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{doc.title}</p>
                          <p className="text-[11px] text-white/35">{doc.repo_name}</p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          doc.status === 'complete'   ? 'bg-emerald-500/15 text-emerald-400' :
                          doc.status === 'generating' ? 'bg-blue-500/15 text-blue-400' :
                          'bg-red-500/15 text-red-400'
                        }`}>{doc.status}</span>
                      </div>
                    ))}
                  </div>
              }
            </Card>

            {/* Recent Test Plans */}
            <Card>
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/6">
                <h2 className="font-semibold text-white text-sm">Recent Test Plans</h2>
                <Link to="/dashboard/test-plans" className="text-xs text-purple-400 hover:text-purple-300">View all →</Link>
              </div>
              {!activity?.recent_test_plans?.length
                ? <EmptyState icon="🧪" title="No test plans yet" description="Generate a test plan from any connected repository." />
                : <div className="divide-y divide-white/5">
                    {activity.recent_test_plans.slice(0, 5).map(plan => (
                      <div key={plan.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-white/2 transition-colors">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)' }}>
                          <span className="text-sm">🧪</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{plan.title}</p>
                          <p className="text-[11px] text-white/35">{plan.repo_name}</p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${
                          plan.status === 'complete'   ? 'bg-emerald-500/15 text-emerald-400' :
                          plan.status === 'generating' ? 'bg-blue-500/15 text-blue-400' :
                          'bg-red-500/15 text-red-400'
                        }`}>{plan.status}</span>
                      </div>
                    ))}
                  </div>
              }
            </Card>
          </div>

          {/* Quick actions */}
          <Card>
            <div className="px-5 py-4 border-b border-white/6">
              <h2 className="font-semibold text-white text-sm">Quick Actions</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5">
              {[
                { icon: '⬡', label: 'New Project',       sub: 'Create a project workspace',     to: '/dashboard/projects' },
                { icon: '📄', label: 'Generate Document', sub: 'Create business documentation',   to: '/dashboard/documents' },
                { icon: '🧪', label: 'Create Test Plan',  sub: 'Auto-generate QA test cases',     to: '/dashboard/test-plans' },
              ].map(action => (
                <Link key={action.label} to={action.to}
                  className="flex items-center gap-4 p-4 rounded-xl border border-white/6 hover:border-purple-500/30 hover:bg-white/3 transition-all group">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: 'rgba(139,63,255,0.12)', border: '1px solid rgba(139,63,255,0.2)' }}>
                    {action.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white group-hover:text-purple-300 transition-colors">{action.label}</p>
                    <p className="text-[11px] text-white/35 mt-0.5">{action.sub}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
